'use client';

import React, { ReactNode } from 'react';

import {
    Alignable,
    Column,
    Columns,
    Divider,
    Icon,
    IconColor,
    IconName,
    Justifiable,
    Section,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
    TypographyTransform,
    View,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import { ProductChildFull } from '@app-types/product';

import { isFirstLetterVowel } from '@helpers/wording';

import { EnergyLabel, Reparability } from '@product-detail/components';

import './characteristics.css';

export type CharacteristicsItemProps = {
    name: string;
    value?: string | boolean;
    unit?: string;
};

export type CharacteristicsBoxProps = {
    title: string;
    children: ReactNode;
};

export type CharacteristicsGroup = {
    name: string;
    characteristics?: CharacteristicsItemProps[];
    children?: ReactNode;
    note?: string;
};

const getCharacteristicsGroups = (product: ProductChildFull): CharacteristicsGroup[] => [
    {
        name: 'Chargeur secteur',
        children:
            product.characteristics.charging !== undefined ? (
                <View flexable align={Alignable.ALIGNED_CENTER} className='gap-medium'>
                    <img
                        src={`${getAppConfig().assets.cmsImages.url}/product-details/charging_head${product.characteristics.charging.head ? '_' : '_not_'}included.svg`}
                        alt=''
                        width={80}
                        loading='lazy'
                    />
                    <Text level={TextLevels.TWO} marginless>
                        {product.characteristics.charging.head
                            ? 'Tête de charge incluse'
                            : 'Pas de tête de charge incluse'}
                    </Text>
                </View>
            ) : null,
    },
    {
        name: 'Puissance de chargement',
        children:
            product.characteristics.charging?.minLoadPower && product.characteristics.charging?.maxLoadPower ? (
                <View flexable align={Alignable.ALIGNED_CENTER} className='gap-medium'>
                    <img
                        src={`${getAppConfig().assets.cmsImages.url}/product-details/charging_head_values.svg`}
                        alt=''
                        width={80}
                        loading='lazy'
                    />
                    <p className='charging-head-values charging-head-min' aria-hidden='true'>
                        {product.characteristics.charging.minLoadPower}
                    </p>
                    <p className='charging-head-values charging-head-max' aria-hidden='true'>
                        {product.characteristics.charging.maxLoadPower}
                    </p>
                    <Text level={TextLevels.TWO} marginless>
                        La puissance fournie par le chargeur doit être entre, au minimum,{' '}
                        {product.characteristics.charging.minLoadPower} Watts requis par l&apos;équipement
                        radioélectrique et, au maximum, {product.characteristics.charging.maxLoadPower} Watts pour
                        atteindre la vitesse de chargement maximale.
                    </Text>
                </View>
            ) : null,
    },
    {
        name: 'Inclus dans le coffret',
        children: product.characteristics.included ? (
            <p dangerouslySetInnerHTML={{ __html: product.characteristics.included }} />
        ) : null,
    },
    {
        name: 'Non inclus dans le coffret',
        children: product.characteristics.nonIncluded ? (
            <p dangerouslySetInnerHTML={{ __html: product.characteristics.nonIncluded }} />
        ) : null,
    },
    {
        name: 'Étiquette énergétique',
        children: product.characteristics.energyLabel ? (
            <EnergyLabel energyLabel={product.characteristics.energyLabel} />
        ) : null,
    },
    {
        name: 'Indice de réparabilité',
        children: product.reparability ? <Reparability reparability={product.reparability} /> : null,
    },
    {
        name: 'OS',
        characteristics: [
            { name: 'Système', value: product.characteristics.os?.name },
            { name: "Version de l'OS", value: product.characteristics.os?.version },
        ],
    },
    {
        name: 'Réseau',
        characteristics: [
            { name: 'Connectivité max', value: product.characteristics.connectionRange },
            { name: 'Wifi', value: product.characteristics.wifi },
        ],
    },
    {
        name: 'Carte SIM',
        characteristics: [
            { name: 'Multi SIM', value: product.characteristics.isSimMultiple },
            { name: 'Compatibilité eSIM', value: product.characteristics.isESimCompatible },
        ],
    },
    {
        name: 'Écran',
        characteristics: [
            { name: 'Écran', value: product.characteristics.diagonal, unit: 'pouces' },
            { name: "Largeur de l'écran", value: product.characteristics.resolution?.width, unit: 'pixels' },
            { name: "Hauteur de l'écran", value: product.characteristics.resolution?.height, unit: 'pixels' },
        ],
    },
    {
        name: 'Photo',
        characteristics: [
            { name: 'Photo', value: product.characteristics.resolution?.sensor, unit: 'Mpx' },
            { name: 'Auto-focus', value: product.characteristics.hasAutoFocus },
            { name: 'Double caméra', value: product.characteristics.isDoubleCamera },
        ],
    },
    {
        name: 'Batterie',
        characteristics: [
            { name: 'Capacité de la batterie', value: product.characteristics.battery?.capacity, unit: 'mAh' },
            { name: 'Batterie Amovible', value: product.characteristics.battery?.isRemovable },
            { name: 'Autonomie en communication', value: product.characteristics.battery?.talkTime, unit: 'h' },
            { name: 'Autonomie', value: product.characteristics.battery?.standbyTime, unit: 'h' },
        ],
    },
    {
        name: 'Stockage',
        characteristics: [
            { name: 'Mémoire', value: product.capacity },
            { name: 'Mémoire disponible estimée', value: product.characteristics.capacityAvailable },
            { name: 'Capacité SD', value: product.characteristics.capacitySd },
        ],
    },
    {
        name: 'Processeur',
        characteristics: [
            { name: 'Processeur nombre de coeur', value: product.characteristics.processor?.core },
            { name: 'Vitesse du processeur', value: product.characteristics.processor?.speed, unit: 'Ghz' },
        ],
    },
    {
        name: 'Encombrement',
        characteristics: [
            { name: 'Poids', value: product.characteristics.weight, unit: 'g' },
            { name: 'Longueur', value: product.characteristics.length, unit: 'cm' },
            { name: 'Largeur', value: product.characteristics.width, unit: 'cm' },
            { name: 'Hauteur', value: product.characteristics.height, unit: 'cm' },
        ],
    },
    {
        name: 'DAS',
        characteristics: product.das.map((das) => ({
            name: das.name,
            value: das.value,
            unit: 'W/kg',
        })),
        note: "Le débit d'absorption spécifique (DAS) local quantifie l'exposition de l’utilisateur aux ondes électromagnétiques de l'équipement concerné. Le DAS maximal autorisé est de 2 W/kg pour la tête et le tronc et de 4 W/kg pour les membres.",
    },
    {
        name: 'Sortie',
        characteristics: [
            { name: 'Sortie carte mémoire', value: product.characteristics.output?.memoryCard },
            { name: 'Sortie chargeur', value: product.characteristics.output?.charger },
            { name: 'Sortie audio', value: product.characteristics.output?.audio },
        ],
    },
    {
        name: 'Garantie',
        characteristics: [{ name: 'Durée de la garantie légale de conformité', value: '2', unit: 'ans' }],
    },
];

function CharacteristicsItem({ name, value, unit }: CharacteristicsItemProps) {
    let valueContent;
    switch (typeof value) {
        case 'string':
            valueContent = `${value} ${unit || ''}`;
            break;
        case 'boolean':
            valueContent = value ? (
                <Icon name={IconName.CHECK} color={IconColor.SUCCESS} />
            ) : (
                <Icon name={IconName.TIMES} color={IconColor.ERROR} />
            );
            break;
    }

    return (
        <View flexable justify={Justifiable.SPACE_BETWEEN} align={Alignable.ALIGNED_CENTER} className='gap-medium'>
            <Text level={TextLevels.TWO} marginless>
                {name}
            </Text>
            <Text
                typo={[TypographyAlign.TEXT_RIGHT, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                level={TextLevels.TWO}
                className='is-nowrap'
            >
                {valueContent}
            </Text>
        </View>
    );
}

function CharacteristicsBox({ title, children }: CharacteristicsBoxProps) {
    return (
        <Column size={4}>
            <Text
                typo={[TypographyTransform.TEXT_UPPERCASE, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                level={TextLevels.TWO}
            >
                {title}
            </Text>
            <Divider />
            <View flexable className='is-flex-col gap-medium'>
                {children}
            </View>
            <Spacer size={SpacerSize.EIGHT} />
        </Column>
    );
}

export type CharacteristicsTabProps = {
    product: ProductChildFull;
};

export function Characteristics({ product }: CharacteristicsTabProps) {
    const { name } = product;

    const articlePartitif = isFirstLetterVowel(name) ? "de l'" : 'du ';

    const characteristicsGroups = getCharacteristicsGroups(product);

    return (
        <>
            <Title level={TitleLevels.FOUR}>
                Les caractéristiques {articlePartitif}
                {name}
            </Title>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Columns multiline>
                    {characteristicsGroups.map(({ name, characteristics, note, children }) => {
                        const definedCharacteristics =
                            characteristics?.filter(({ value }) => value !== undefined) ?? [];
                        if (definedCharacteristics.length > 0 || children) {
                            return (
                                <CharacteristicsBox title={name} key={name}>
                                    {children}
                                    {definedCharacteristics?.map(({ name, value, unit }) => (
                                        <CharacteristicsItem key={name} name={name} value={value} unit={unit} />
                                    ))}
                                    <Text level={TextLevels.TWO}>{note}</Text>
                                </CharacteristicsBox>
                            );
                        }
                    })}
                </Columns>
            </Section>
        </>
    );
}
